import { useCallback } from 'react';

const FOCUS_RESTORATION_RETRY_DELAY_MS = 50;
const FOCUS_RESTORATION_MAX_ATTEMPTS = 10;

function scrollContainerTo(scrollContainer, top) {
  if (!scrollContainer.scrollTo) {
    // eslint-disable-next-line no-param-reassign
    scrollContainer.scrollTop = top;
    return;
  }

  try {
    scrollContainer.scrollTo({ top, behavior: 'auto' });
  } catch {
    scrollContainer.scrollTo(0, top);
  }
}

function scrollTargetIntoContainer(focusTarget, scrollContainer) {
  if (!scrollContainer) {
    focusTarget.scrollIntoView?.({ block: 'center' });
    return;
  }

  const focusTargetRect = focusTarget.getBoundingClientRect();
  const scrollContainerRect = scrollContainer.getBoundingClientRect();
  const focusTargetCenterOffset = focusTargetRect.top - scrollContainerRect.top - (scrollContainerRect.height - focusTargetRect.height) / 2;

  const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  const nextScrollTop = Math.min(maxScrollTop, Math.max(0, scrollContainer.scrollTop + focusTargetCenterOffset));

  scrollContainerTo(scrollContainer, nextScrollTop);
}

export function restoreConditionsFocus(focusTargetId, { scrollContainer } = {}) {
  const focusTarget = focusTargetId ? document.getElementById(focusTargetId) : null;
  if (!focusTarget) return false;

  focusTarget.focus?.({ preventScroll: true });
  // A TabPanel can mount its children before its active state has made them focusable. In that
  // case `focus()` leaves focus on the application skip-link. Do not consume the marker yet:
  // retry once the panel's link can genuinely receive focus, then calculate its scroll position.
  if (document.activeElement !== focusTarget) return false;

  scrollTargetIntoContainer(focusTarget, scrollContainer);
  return true;
}

export function scheduleConditionsFocusRestoration(restoration, consumeRestoration, options = {}) {
  if (!restoration?.focusTargetId) return undefined;

  let attempts = 0;
  let timeoutId;

  const restoreOrRetry = () => {
    attempts += 1;
    const didRestore = restoreConditionsFocus(restoration.focusTargetId, options);

    if (didRestore || attempts >= FOCUS_RESTORATION_MAX_ATTEMPTS) {
      consumeRestoration();
      return;
    }

    timeoutId = setTimeout(restoreOrRetry, FOCUS_RESTORATION_RETRY_DELAY_MS);
  };

  timeoutId = setTimeout(restoreOrRetry);

  return () => clearTimeout(timeoutId);
}

export default function useConditionsFocusRestoration(setRestoration) {
  return useCallback(
    (focusTargetId) => {
      if (!focusTargetId) return;
      setRestoration({ focusTargetId });
    },
    [setRestoration]
  );
}
